
import { useState, useEffect, useRef } from 'react';

interface UseProgressAnimationProps {
  progress: number;
  duration?: number;
  preventBackwards?: boolean;
  skipIfRecycled?: boolean;
  isRecycled?: boolean;
}

/**
 * Hook for smoothly animating progress values
 * Used by both LearningProgress and ProgressBar components
 */
export const useProgressAnimation = ({ 
  progress, 
  duration = 800, 
  preventBackwards = true,
  skipIfRecycled = true,
  isRecycled = false
}: UseProgressAnimationProps) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const prevProgressRef = useRef(0);
  
  useEffect(() => {
    const currentProgress = prevProgressRef.current;
    
    // Don't animate backwards for recycled items if configured
    if (skipIfRecycled && isRecycled && progress <= currentProgress) {
      console.debug(`[useProgressAnimation] Keeping progress at ${currentProgress}% for recycled item`);
      return;
    }
    
    let start = animatedProgress;
    
    // Prevent backwards animation in general if configured
    if (preventBackwards && start > progress) {
      console.debug(`[useProgressAnimation] Preventing backwards animation: ${start}% -> ${progress}%`);
      setAnimatedProgress(progress);
      prevProgressRef.current = progress;
      return;
    }
    
    // Animate progress smoothly
    let startTime: number;
    
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      
      const t = Math.min(elapsed / duration, 1);
      const easeT = t * (2 - t); // easeOutQuad
      const newProgress = start + (progress - start) * easeT;
      
      setAnimatedProgress(newProgress);
      
      if (elapsed < duration) {
        window.requestAnimationFrame(step);
      } else {
        prevProgressRef.current = progress;
      }
    };
    
    window.requestAnimationFrame(step);
  }, [progress, animatedProgress, duration, preventBackwards, skipIfRecycled, isRecycled]);
  
  return { animatedProgress };
};
